<script lang="ts">
  import { route } from "@mateothegreat/svelte5-router";
  import { RefreshCw, Wallet } from "lucide-svelte";
  import SolanaShell from "../../components/SolanaShell.svelte";
  import { shortAddress } from "../../../utils";
  import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL, VersionedTransaction } from "@solana/web3.js";
  import {
    getAssociatedTokenAddress,
    createAssociatedTokenAccountInstruction,
    createSyncNativeInstruction,
    createCloseAccountInstruction,
    NATIVE_MINT,
  } from "@solana/spl-token";

  import { updateMeta, injectJsonLd } from "../../seo/meta";
  import { ROUTE_SEO } from "../../seo/routes";
  import { ORGANIZATION_SCHEMA, WRAPSOL_APP_SCHEMA, WRAPSOL_FAQ_SCHEMA, combineSchemas, getBreadcrumbSchema } from "../../seo/schemas";
  updateMeta(ROUTE_SEO["/tools/solana/wrap-sol"]);
  injectJsonLd(combineSchemas(
    ORGANIZATION_SCHEMA,
    WRAPSOL_APP_SCHEMA,
    WRAPSOL_FAQ_SCHEMA,
    getBreadcrumbSchema([
      { name: "Home", url: "https://0xfire.com/" },
      { name: "Tools", url: "https://0xfire.com/tools" },
      { name: "Solana", url: "https://0xfire.com/tools/solana" },
      { name: "Wrap SOL", url: "https://0xfire.com/tools/solana/wrap-sol" },
    ]),
  ));

  let wallet: any = $state(null);
  let publicKey: string | null = $state(null);
  let connected = $state(false);
  let solBalance = $state(0);
  let wsolBalance = $state(0);
  let loading = $state(false);
  let txStatus = $state<{ type: "success" | "error"; message: string } | null>(null);
  let mode: "wrap" | "unwrap" = $state("wrap");
  let wrapAmount = $state("");
  let noWallet = $state(false);
  let rpcInput = $state("https://solana-rpc.publicnode.com");

  // Connection created lazily from user-provided RPC
  function getConnection(): Connection {
    return new Connection(rpcInput, "confirmed");
  }

  import { onMount, onDestroy } from "svelte";

  function handleAccountChanged(newPubkey: any) {
    if (newPubkey) {
      publicKey = newPubkey.toString();
      refreshBalances();
    } else {
      // Wallet disconnected or locked
      disconnectWallet();
    }
  }

  onMount(() => {
    const provider = getProvider();
    if (provider) {
      connectWallet();
      provider.on?.("accountChanged", handleAccountChanged);
    }
  });

  onDestroy(() => {
    const provider = getProvider();
    provider?.removeListener?.("accountChanged", handleAccountChanged);
  });

  function getProvider(): any {
    const w = (window as any);
    if (w.phantom?.solana?.isPhantom) return w.phantom.solana;
    if (w.solana?.isPhantom) return w.solana;
    if (w.solflare?.isSolflare) return w.solflare;
    return null;
  }

  async function connectWallet() {
    const provider = getProvider();
    if (!provider) {
      noWallet = true;
      return;
    }
    try {
      const resp = await provider.connect();
      publicKey = resp.publicKey.toString();
      wallet = provider;
      connected = true;
      noWallet = false;
      await refreshBalances();
    } catch (e: any) {
      txStatus = { type: "error", message: e.message ?? "Failed to connect wallet" };
    }
  }

  async function disconnectWallet() {
    if (wallet) {
      try { await wallet.disconnect(); } catch {}
    }
    wallet = null;
    publicKey = null;
    connected = false;
    solBalance = 0;
    wsolBalance = 0;
    txStatus = null;
  }

  async function refreshBalances() {
    if (!publicKey) return;
    const connection = getConnection();
    try {
      const owner = new PublicKey(publicKey);
      solBalance = (await connection.getBalance(owner)) / LAMPORTS_PER_SOL;

      const ata = await getAssociatedTokenAddress(NATIVE_MINT, owner);
      try {
        const tokenBal = await connection.getTokenAccountBalance(ata);
        wsolBalance = parseFloat(tokenBal.value.uiAmountString ?? "0");
      } catch {
        wsolBalance = 0;
      }
    } catch (e: any) {
      txStatus = { type: "error", message: "Failed to fetch balances. Check your RPC URL." };
    }
  }

  function setMax() {
    const max = Math.max(0, solBalance - 0.01);
    wrapAmount = max > 0 ? max.toFixed(6) : "0";
  }

  async function sendTx(tx: Transaction): Promise<string> {
    // Use wallet's signAndSendTransaction — the wallet uses its own RPC, bypassing CORS
    const owner = new PublicKey(publicKey!);
    tx.feePayer = owner;
    const connection = getConnection();
    tx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;

    // Phantom supports signAndSendTransaction which uses its own RPC
    if (wallet.signAndSendTransaction) {
      const { signature } = await wallet.signAndSendTransaction(tx);
      return signature;
    }

    // Fallback: sign + send manually
    const signed = await wallet.signTransaction(tx);
    const sig = await connection.sendRawTransaction(signed.serialize());
    return sig;
  }

  async function wrapSol() {
    if (!publicKey || !wallet) return;
    const amount = parseFloat(wrapAmount);
    if (isNaN(amount) || amount <= 0) {
      txStatus = { type: "error", message: "Enter a valid amount" };
      return;
    }
    if (amount > solBalance - 0.005) {
      txStatus = { type: "error", message: "Insufficient SOL (need some for fees)" };
      return;
    }

    loading = true;
    txStatus = null;

    try {
      const owner = new PublicKey(publicKey);
      const ata = await getAssociatedTokenAddress(NATIVE_MINT, owner);
      const connection = getConnection();

      const tx = new Transaction();

      const ataInfo = await connection.getAccountInfo(ata);
      if (!ataInfo) {
        tx.add(createAssociatedTokenAccountInstruction(owner, ata, owner, NATIVE_MINT));
      }

      tx.add(
        SystemProgram.transfer({
          fromPubkey: owner,
          toPubkey: ata,
          lamports: Math.floor(amount * LAMPORTS_PER_SOL),
        }),
      );
      tx.add(createSyncNativeInstruction(ata));

      const sig = await sendTx(tx);
      txStatus = { type: "success", message: sig };
      wrapAmount = "";
      // Wait a bit for confirmation then refresh
      setTimeout(() => refreshBalances(), 3000);
    } catch (e: any) {
      txStatus = { type: "error", message: e.message ?? "Transaction failed" };
    } finally {
      loading = false;
    }
  }

  async function unwrapSol() {
    if (!publicKey || !wallet) return;
    if (wsolBalance <= 0) {
      txStatus = { type: "error", message: "No wSOL to unwrap" };
      return;
    }

    loading = true;
    txStatus = null;

    try {
      const owner = new PublicKey(publicKey);
      const ata = await getAssociatedTokenAddress(NATIVE_MINT, owner);

      const tx = new Transaction();
      tx.add(createCloseAccountInstruction(ata, owner, owner));

      const sig = await sendTx(tx);
      txStatus = { type: "success", message: sig };
      setTimeout(() => refreshBalances(), 3000);
    } catch (e: any) {
      txStatus = { type: "error", message: e.message ?? "Transaction failed" };
    } finally {
      loading = false;
    }
  }
</script>

<SolanaShell activePage="wrap-sol">
  <!-- Hero -->
  <div class="text-center mb-8">
    <h1 class="text-2xl font-bold text-white tracking-tight">
      Wrap & Unwrap SOL
    </h1>
    <p class="text-sm text-gray-500 mt-1">Convert between SOL and Wrapped SOL (wSOL)</p>
  </div>

  <!-- Tool Card — centered -->
  <div class="max-w-md mx-auto">
    <div class="bg-gray-950/40 border border-gray-800 rounded-sm overflow-hidden">
      {#if !connected}
        <!-- Connect Wallet -->
        <div class="p-8 text-center">
          <Wallet class="size-10 text-gray-600 mx-auto mb-4" />
          <p class="text-gray-500 text-sm mb-6">Connect your wallet to get started</p>
          <button
            onclick={connectWallet}
            class="btn bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-widest font-bold transition-colors px-8"
          >
            Connect Wallet
          </button>
          {#if noWallet}
            <p class="text-red-500 text-xs mt-4">
              No Solana wallet found. Install <a href="https://phantom.app" target="_blank" class="underline hover:text-red-400">Phantom</a> or <a href="https://solflare.com" target="_blank" class="underline hover:text-red-400">Solflare</a>.
            </p>
          {/if}
        </div>
      {:else}
        <!-- Wallet Info -->
        <div class="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="w-2 h-2 rounded-full bg-green-500"></span>
            <span class="text-xs font-mono text-gray-400">{shortAddress(publicKey ?? "", 6, 4)}</span>
          </div>
          <div class="flex items-center gap-2">
            <button
              onclick={refreshBalances}
              class="btn btn-xs btn-ghost text-gray-500 hover:text-white rounded-sm"
              title="Refresh balances"
            >
              <RefreshCw class="size-3.5" />
            </button>
            <button
              onclick={disconnectWallet}
              class="text-xs text-gray-600 hover:text-gray-400 font-mono transition-colors"
            >
              Disconnect
            </button>
          </div>
        </div>

        <!-- Balances -->
        <div class="grid grid-cols-2 divide-x divide-gray-800 border-b border-gray-800">
          <div class="px-6 py-4 text-center">
            <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">SOL</p>
            <p class="text-xl font-black text-white tabular-nums">{solBalance.toFixed(4)}</p>
          </div>
          <div class="px-6 py-4 text-center">
            <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">wSOL</p>
            <p class="text-xl font-black text-white tabular-nums">{wsolBalance.toFixed(4)}</p>
          </div>
        </div>

        <!-- Tabs -->
        <div class="flex border-b border-gray-800">
          <button
            class="flex-1 py-3 text-center text-xs font-mono uppercase tracking-widest transition-colors {mode === 'wrap'
              ? 'text-orange-500 border-b-2 border-orange-500 bg-orange-500/5'
              : 'text-gray-500 hover:text-gray-300'}"
            onclick={() => { mode = "wrap"; txStatus = null; }}
          >
            Wrap SOL → wSOL
          </button>
          <button
            class="flex-1 py-3 text-center text-xs font-mono uppercase tracking-widest transition-colors {mode === 'unwrap'
              ? 'text-orange-500 border-b-2 border-orange-500 bg-orange-500/5'
              : 'text-gray-500 hover:text-gray-300'}"
            onclick={() => { mode = "unwrap"; txStatus = null; }}
          >
            Unwrap wSOL → SOL
          </button>
        </div>

        <!-- Action Area -->
        <div class="p-6">
          {#if mode === "wrap"}
            <div class="mb-4">
              <div class="flex items-center justify-between mb-1">
                <span class="text-[10px] font-mono text-gray-600 uppercase tracking-widest">Amount (SOL)</span>
                <button onclick={setMax} class="text-[10px] font-mono text-orange-500 hover:text-orange-400 transition-colors">MAX</button>
              </div>
              <input
                type="number"
                class="input bg-black border-gray-800 text-white font-mono text-lg w-full rounded-sm"
                placeholder="0.00"
                step="0.001"
                min="0"
                bind:value={wrapAmount}
              />
            </div>
            <button
              onclick={wrapSol}
              disabled={loading}
              class="btn bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-widest font-bold transition-colors w-full"
            >
              {loading ? "Wrapping..." : "Wrap SOL"}
            </button>
          {:else}
            <div class="mb-4">
              <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">wSOL to unwrap</p>
              <p class="text-2xl font-black text-white tabular-nums">{wsolBalance.toFixed(6)} <span class="text-sm text-gray-500 font-normal">wSOL</span></p>
              <p class="text-[10px] text-gray-600 mt-1">Unwrapping closes the wSOL account and returns all SOL to your wallet.</p>
            </div>
            <button
              onclick={unwrapSol}
              disabled={loading || wsolBalance <= 0}
              class="btn bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-widest font-bold transition-colors w-full"
            >
              {loading ? "Unwrapping..." : "Unwrap All wSOL"}
            </button>
          {/if}
        </div>

        <!-- Status -->
        {#if txStatus}
          <div class="px-6 pb-4">
            {#if txStatus.type === "success"}
              <div class="bg-green-500/10 border border-green-500/20 rounded-sm p-3">
                <p class="text-xs text-green-500 font-mono mb-1">Transaction sent</p>
                <a
                  href={`https://solscan.io/tx/${txStatus.message}`}
                  target="_blank"
                  class="text-[10px] text-green-500/70 hover:text-green-500 font-mono transition-colors break-all"
                >
                  {txStatus.message}
                </a>
              </div>
            {:else}
              <div class="bg-red-500/10 border border-red-500/20 rounded-sm p-3">
                <p class="text-xs text-red-500 font-mono">{txStatus.message}</p>
              </div>
            {/if}
          </div>
        {/if}
      {/if}
    </div>

    <!-- RPC (collapsible) -->
    <details class="mt-3">
      <summary class="text-[10px] font-mono text-gray-700 uppercase tracking-widest hover:text-gray-500 transition-colors">
        Advanced: RPC Endpoint
      </summary>
      <input
        type="text"
        class="input input-sm bg-black border-gray-800 text-white font-mono text-xs w-full rounded-sm mt-2"
        bind:value={rpcInput}
      />
    </details>
  </div>

  <!-- SEO Content — centered, wider -->
  <div class="max-w-2xl mx-auto mt-16">
    <div class="border-t border-gray-800 pt-10">
      <h2 class="text-lg font-bold text-white mb-4">How to Wrap SOL to wSOL</h2>
      <p class="text-sm text-gray-500 leading-relaxed mb-6">
        <strong class="text-gray-300">Wrapped SOL (wSOL)</strong> is the SPL token version of native SOL. DeFi protocols on Solana — Jupiter, Raydium, Orca, and others — require wSOL for swaps, liquidity provision, and staking. This tool lets you convert instantly by connecting your wallet.
      </p>

      <ol class="space-y-3 text-sm text-gray-500 mb-10">
        <li class="flex gap-3">
          <span class="text-orange-500 font-bold shrink-0">1.</span>
          <span><strong class="text-gray-300">Connect your wallet</strong> — Phantom, Solflare, Backpack, or any Solana browser wallet. Auto-connects on page load.</span>
        </li>
        <li class="flex gap-3">
          <span class="text-orange-500 font-bold shrink-0">2.</span>
          <span><strong class="text-gray-300">Enter amount</strong> — choose how much SOL to wrap. Use MAX to wrap everything minus a small fee reserve.</span>
        </li>
        <li class="flex gap-3">
          <span class="text-orange-500 font-bold shrink-0">3.</span>
          <span><strong class="text-gray-300">Confirm in your wallet</strong> — approve the transaction. Your wSOL balance updates within seconds.</span>
        </li>
      </ol>
    </div>

    <div class="border-t border-gray-800 pt-10">
      <h2 class="text-lg font-bold text-white mb-6">Frequently Asked Questions</h2>
      <div class="space-y-6">
        <div>
          <h3 class="text-sm font-bold text-white mb-1">What is Wrapped SOL (wSOL)?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">The SPL token version of native SOL, with mint address <code class="text-xs text-gray-400 bg-gray-900 px-1 py-0.5 rounded">So111...112</code>. All major Solana DeFi protocols use it for a uniform token interface.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Why do I need to wrap SOL?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">DEXs like Jupiter, Raydium, and Orca require wSOL for swaps, liquidity provision, and staking. Wrapping lets you use SOL like any other SPL token.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Is wrapping SOL safe?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Yes — it's a native Solana operation. Your SOL moves into an Associated Token Account that you own. Funds never leave your wallet. Unwrap anytime.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Does it cost anything?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Only the standard Solana transaction fee (~0.000005 SOL). If you don't have a wSOL account yet, the first wrap pays ~0.002 SOL rent deposit, which you get back when you unwrap.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Can I unwrap only part of my wSOL?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">This tool unwraps all wSOL by closing the token account. To keep some wSOL, re-wrap the desired amount after unwrapping.</p>
        </div>
        <div>
          <h3 class="text-sm font-bold text-white mb-1">Which wallets are supported?</h3>
          <p class="text-sm text-gray-500 leading-relaxed">Phantom, Solflare, Backpack, and any browser wallet that injects a standard Solana provider. The tool auto-connects when you load the page.</p>
        </div>
      </div>
    </div>
  </div>
</SolanaShell>
